/**
 * Allowed query parameters that can be passed through the lead capture flow
 * Add any custom parameters your business needs here
 *
 * IMPORTANT: This includes critical tracking parameters for:
 * - Google Analytics cross-domain tracking (_ga, _gid, _gl, _gac_*)
 * - Google Ads conversion tracking (gclid)
 * - Other platform tracking (fbclid, msclkid, etc.)
 *
 * Removing these will break cross-domain analytics tracking!
 */
export const ALLOWED_QUERY_PARAMS = [
    // Standard UTM parameters
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',

    // Google Analytics cross-domain tracking
    '_ga', // GA client ID
    '_gid', // GA user distinction
    '_gl', // GA Linker parameter
    '_gac', // GA campaign data (note: may have suffixes like _gac_UA-XXXXX)
    'gclid', // Google Ads Click ID

    // Other tracking platforms
    'fbclid', // Facebook Click ID
    'msclkid', // Microsoft Ads Click ID
    'ttclid', // TikTok Click ID
    'li_fat_id', // LinkedIn first-party tracking

    // Custom business parameters
    'partner_name',
    'partner_id',
    'email',
    'referral_code',
    'promo_code',
    'channel',
    'sub_channel',

    // A/B testing params
    'experiment_id',
    'variant_id',

    // Add more as needed
] as const;

// Type for the allowed params
export type AllowedQueryParam = (typeof ALLOWED_QUERY_PARAMS)[number];

/**
 * Checks if a parameter name matches allowed patterns
 * @param paramName - The parameter name to check
 * @returns Whether the parameter is allowed
 */
const isAllowedParam = (paramName: string): boolean => {
    // Direct match
    if (ALLOWED_QUERY_PARAMS.includes(paramName as any)) {
        return true;
    }

    // Pattern matching for dynamic parameters
    // Google Analytics _gac_<property-id> pattern
    if (/^_gac_[A-Z0-9-]+$/i.test(paramName)) {
        return true;
    }

    return false;
};

/**
 * Extracts only allowed query parameters from URLSearchParams
 * @param searchParams - The URLSearchParams object to filter
 * @returns Object containing only allowed parameters
 */
export const extractAllowedParams = (
    searchParams: URLSearchParams | undefined
): Record<string, string | null> => {
    if (!searchParams) return {};

    const params: Record<string, string | null> = {};

    // Check each parameter in the searchParams
    searchParams.forEach((value, key) => {
        if (isAllowedParam(key)) {
            params[key] = value;
        }
    });

    return params;
};

/**
 * Validates and sanitizes parameter values
 * @param params - Parameters to validate
 * @returns Sanitized parameters
 */
export const sanitizeParams = (
    params: Record<string, string | null>
): Record<string, string | null> => {
    const sanitized: Record<string, string | null> = {};

    Object.entries(params).forEach(([key, value]) => {
        if (value === null) return;

        // Basic sanitization - remove any HTML tags and limit length
        let cleanValue = value
            .replace(/<[^>]*>/g, '') // Remove HTML tags
            .trim()
            .slice(0, 100); // Limit length to 100 chars

        // Additional validation for specific params
        switch (key) {
            case 'partner_id':
            case 'experiment_id':
            case 'variant_id':
                // These should be alphanumeric only
                cleanValue = cleanValue.replace(/[^a-zA-Z0-9_-]/g, '');
                break;
            case 'referral_code':
            case 'promo_code':
                // These might have special formats
                cleanValue = cleanValue.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                break;
            // Analytics tracking IDs - preserve their format
            case '_ga':
            case '_gid':
            case '_gl':
            case 'gclid':
            case 'fbclid':
            case 'msclkid':
            case 'ttclid':
            case 'li_fat_id':
                // These have specific formats, just do basic length limiting
                cleanValue = value.slice(0, 200); // Tracking IDs can be longer
                break;
            default:
                // Handle dynamic patterns like _gac_UA-XXXXX
                if (key.startsWith('_gac_')) {
                    cleanValue = value.slice(0, 200);
                }
                break;
        }

        sanitized[key] = cleanValue;
    });

    return sanitized;
};
